import * as z from "zod";
import { zql } from "../generatedDrizzleSchema";
import {
  grantPrincipalResolve,
  grantRoleResolve,
} from "../rbac/grantResources";
import { grantEntry, validateGrants } from "../rbac/validation";
import { mutator } from "./mutator";
import { generateId } from "../id";
import { clientGeneratableIdSchema } from "../id";

export const requestScopeRoleGrantsSchema = z.object({
  requestId: clientGeneratableIdSchema,
  scopeId: z.string(),
  grants: z.array(grantEntry),
  note: z.string().optional(),
});

// The asynchronous twin of patchScopeGrants — connector-only for now: a
// principal who can configure a connector but can't manage its access list
// records the grants they'd like as a pending request for a reviewer to approve
// (see approveGrantRequest). Each proposed grant is a grantRequestGrants row.
export const requestScopeRoleGrants = mutator({
  schema: requestScopeRoleGrantsSchema,
  resolve: {
    connector: { kind: "connectedApp", from: "scopeId" },
    ...grantPrincipalResolve,
    ...grantRoleResolve,
  },
  permissions: [
    {
      permission: "connected_app:write",
      scopeId: ({ args }) => args.connector.id,
    },
  ],
  fn: async ({
    tx,
    ctx,
    args: { requestId, connector, grants, note, grantCustomRoles },
  }) => {
    validateGrants(grants, "connected_app", grantCustomRoles);

    // Replace only this requester's own request for the connector, so a
    // re-submit never discards another user's unreviewed request. The proposed
    // grant rows cascade with the request row.
    const existing = await tx.run(
      zql.grantRequests
        .where("scopeId", connector.id)
        .where("requestedByUserId", ctx.userId)
    );
    for (const row of existing) {
      await tx.mutate.grantRequests.delete({ id: row.id });
    }

    await tx.mutate.grantRequests.insert({
      id: requestId,
      scopeId: connector.id,
      scopeKind: "connected_app",
      note: note ?? null,
      requestedByUserId: ctx.userId,
      createdAt: Date.now(),
      tenantId: ctx.tenantId,
    });

    for (const grant of grants) {
      await tx.mutate.grantRequestGrants.insert({
        id: generateId(),
        requestId,
        userGroupId: grant.userGroupId ?? null,
        userId: grant.userId ?? null,
        serviceAccountId: grant.serviceAccountId ?? null,
        builtinRoleId: grant.builtinRoleId ?? null,
        customRoleId: grant.customRoleId ?? null,
        tenantId: ctx.tenantId,
      });
    }
  },
});
